import React, { useCallback, useRef, useState } from 'react';
import { IInvoiceFullObject } from '../../Interfaces/IInvoice';
import { State } from '../../redux/mainReducer';
import { useSelector } from 'react-redux';
import {
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { IMIMEType } from '../../Interfaces/IMIMEType';
import { IDocument } from '../../Interfaces/IDocument';
import { UploadFile } from '@mui/icons-material';
import { DocumentOverview } from '../../Document/DocumentOverview';
import { IDocumentDownloaded } from '../../Interfaces/IDocumentDownloaded';
import { useCheckDocumentForInvoice } from '../../hooks/reactQuerys/InvoiceQueries';
import { InvoiceTable } from './InvoiceTable';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { ILegalRole } from '../../Interfaces/ILegalRole';
import { ILegalTransactionType } from '../../Interfaces/ILegalTransactionType';
import { ILegalTransaction } from '../../Interfaces/ILegalTransaction';

interface IProps {
  legalTransaction: ILegalTransaction;
  documentArray: IDocument[];
  setDocumentArray: (newDocuments: IDocument[]) => void;
  downloadedArray?: IDocumentDownloaded[];
  invoiceObjectArray: IInvoiceFullObject[];
  setInvoiceObjectArray: Function;
  //
  legalClients: ILegalClient[];
  setLegalClients: Function;
  legalRoles: ILegalRole[];
  legalTransactionTypes: ILegalTransactionType[];
}

export const InvoiceTab: React.FC<IProps> = ({
  legalTransaction,
  documentArray,
  setDocumentArray,
  downloadedArray,
  invoiceObjectArray,
  setInvoiceObjectArray,
  legalClients,
  legalRoles,
  setLegalClients,
  legalTransactionTypes,
}) => {
  const { checkDocumentForInvoice, isPendingCheckDocumentForInvoice } =
    useCheckDocumentForInvoice();
  const mimeTypeArray = useSelector(
    (state: State) => state.mimeTypeArray.storeAcademicTitle
  );
  const [enableUpload, setEnableUpload] = useState(false);
  const inputUpload = useRef<HTMLInputElement | null>(null);

  async function fileToBase64(file: File) {
    const result_base64 = await new Promise(resolve => {
      const fileReader = new FileReader();
      fileReader.onload = () => resolve(fileReader.result);
      fileReader.readAsDataURL(file);
    });
    return String(result_base64).split(',')[1];
  }

  const handleFilesUpload = async (acceptedFiles: File[]) => {
    const uploadArray: IDocument[] = [];
    let minIdOffset = -1;

    if (documentArray.length > 0) {
      const minIdArray = Math.min(
        ...documentArray.map(document => document.idDocument)
      );

      if (minIdArray <= minIdOffset) {
        minIdOffset = minIdArray - 1;
      }
    }

    let index = 0;

    for (const currentFile of acceptedFiles) {
      const fileName: string = String(currentFile['name']);
      const mimeType: IMIMEType | undefined = mimeTypeArray.find(
        mimeType => mimeType.MIMEType === currentFile['type']
      );

      if (mimeType) {
        uploadArray.push({
          idDocument: minIdOffset - index,
          FileName: fileName,
          idMIMEType: mimeType.idMIMEType,
          Data: await fileToBase64(currentFile),
          Created_at: new Date().toISOString(),
        } as IDocument);
      }
      index = index + 1;
    }

    // Bestimme mögliche Zahlungspflichtige
    const assignLegalClients: number[] = [];
    if (legalClients.length === 1) {
      assignLegalClients.push(legalClients[0].idLegalClient);
    } else {
      legalRoles
        .filter(legalRole => legalRole.isPayer == true)
        .map(legalRole =>
          legalClients
            .filter(
              legalClient => legalClient.idLegalRole === legalRole.idLegalRole
            )
            .map(legalClient =>
              assignLegalClients.push(legalClient.idLegalClient)
            )
        );
    }

    const checkedInvocie = await checkDocumentForInvoice(uploadArray);
    const forDocumentArray: IDocument[] = [];
    const forInvoiceArray: IInvoiceFullObject[] = [];

    checkedInvocie.map(invoice => {
      if (invoice.Total === null) {
        forDocumentArray.push({
          idDocument: invoice.idDocument,
          Created_at: new Date().toISOString(),
          FileName: invoice.FileName,
          idDocumentDirectory: null,
          idLegalTransaction: legalTransaction.idLegalTransaction,
          idMIMEType: invoice.idMIMEType,
          Data: invoice.Data,
        } as IDocument);
      } else {
        forInvoiceArray.push({
          ...invoice,
          idLegalTransactionType: legalTransaction.idLegalTransactionType,
          LegalClients: assignLegalClients,
        });
      }
    });
    setDocumentArray([...documentArray, ...forDocumentArray]);
    setInvoiceObjectArray([...invoiceObjectArray, ...forInvoiceArray]);
    setLegalClients([
      ...legalClients.map(legalClient => {
        return {
          ...legalClient,
          enableInvoiceReminding:
            assignLegalClients.indexOf(legalClient.idLegalClient) > -1,
        };
      }),
    ]);
  };

  const handleNormalUpload = (files: null | FileList) => {
    if (files) {
      const acceptedFiles: File[] = Array.from(files);
      handleFilesUpload(acceptedFiles);
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      handleFilesUpload(acceptedFiles);
      setEnableUpload(false);
    },
    [documentArray]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
  });

  const dropZoneTSX = () => {
    return (
      <div>
        <input
          {...getInputProps()}
          accept={String(
            mimeTypeArray.map(mimeType => mimeType.MIMEType).join(', ')
          )}
        />
        <Card>
          <CardContent>
            <Typography variant='body2'>Dokument(e) hier reinziehen</Typography>
            Erlaubte Datentypen
            <br />
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Datentyp</TableCell>
                  <TableCell>Datenendung</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mimeTypeArray.map(mimeType => (
                  <TableRow>
                    <TableCell>{mimeType.MIMEType}</TableCell>
                    <TableCell>{mimeType.FileExtension}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    );
  };

  if (isPendingCheckDocumentForInvoice) {
    return (
      <>
        <Typography variant={'h5'} sx={{ mb: 2, mt: 0 }}>
          Rechnungen
        </Typography>
        Untersuche Dokument(e) ... <CircularProgress />
      </>
    );
  }

  return (
    <div {...getRootProps()}>
      <input
        hidden
        ref={inputUpload}
        type='file'
        accept={String(
          mimeTypeArray.map(mimeType => mimeType.MIMEType).join(', ')
        )}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          handleNormalUpload(event.target.files)
        }
      />

      <Typography variant={'h5'} sx={{ mb: 2, mt: 0 }}>
        Rechnungen
        <IconButton
          title='Daten über Auswähl hochladen'
          onClick={() => inputUpload && inputUpload.current?.click()}
          sx={{ float: 'right' }}
        >
          <UploadFile />
        </IconButton>
        <br />
        <Typography variant='caption'>
          (Hier können Sie Ihre Dokumente per <i>Drag & Drop</i> hochladen)
        </Typography>
      </Typography>

      {(isDragActive || enableUpload) && dropZoneTSX()}

      <Typography variant={'h6'} sx={{ mt: 2 }}>
        Erkannte Rechnungen
      </Typography>
      <InvoiceTable
        idLegalTransaction={legalTransaction.idLegalTransaction}
        downloadedArray={downloadedArray}
        invoiceObjectArray={invoiceObjectArray}
        setInvoiceObjectArray={setInvoiceObjectArray}
        legalClients={legalClients}
        setLegalClients={setLegalClients}
        legalRoles={legalRoles}
        legalTransactionTypes={legalTransactionTypes}
      />

      <DocumentOverview
        key='invoice'
        allowRemove={true}
        allowUpload={false}
        titleVariant='h6'
        marginTop={5}
        title='Nicht erkannte Rechnungen'
        documentArray={documentArray}
        setDocumentArray={setDocumentArray}
        downloadedArray={downloadedArray}
        enableSidepanel={true}
      />
    </div>
  );
};
